import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui";
import {
  Archive,
  ArchiveRestore,
  KeyRound,
  Mail,
  MoreHorizontal,
  Send,
} from "lucide-react";
import {
  buildParcoursDescription,
  dateCourteHeure,
  libelleComposition,
} from "@repo/utils";

interface Evaluation {
  typeEval: string;
  template?: string;
}

interface ParcoursInfoCardProps {
  parcoursId: string;
  prenomCandidat?: string;
  nomCandidat?: string;
  emailCandidat?: string;
  titre: string;
  dateCreation: number;
  dateModification: number;
  lienEnvoye?: boolean;
  archive?: boolean;
  anonyme?: boolean;
  evaluations: Evaluation[];
  peutEnvoyerResultats: boolean;
  onOpenEmailModal: () => void;
  onOpenResultatsModal: () => void;
  onArchive: () => void;
  onDesarchive: () => void;
}

/** Une entrée du menu « … ». Bouton pleine largeur, icône puis libellé. */
function ActionMenu({
  icone,
  onClick,
  children,
}: {
  icone: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Meme rendu qu'un `SelectItem` : meme rayon, meme voile corail au
      // survol. Un menu d'actions et une liste d'options se survolent pareil.
      className="flex w-full items-center gap-2 rounded-full px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-charte-hover-strong hover:text-accent-foreground"
    >
      {icone}
      {children}
    </button>
  );
}

export function ParcoursInfoCard({
  parcoursId,
  prenomCandidat,
  nomCandidat,
  emailCandidat,
  titre,
  dateCreation,
  dateModification,
  lienEnvoye,
  archive,
  anonyme,
  evaluations,
  peutEnvoyerResultats,
  onOpenEmailModal,
  onOpenResultatsModal,
  onArchive,
  onDesarchive,
}: ParcoursInfoCardProps) {
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl">
              {prenomCandidat} {nomCandidat}
            </CardTitle>
            <CardDescription className="mt-1">{emailCandidat}</CardDescription>
          </div>
          {/* Une seule action primaire — envoyer le lien, c'est ce qu'on vient
              faire ici. Le reste part dans un menu : cinq boutons `outline`
              alignés ne hiérarchisaient rien. Les deux actions email portent
              des icônes distinctes (lien / envoi), sans quoi elles étaient
              indiscernables. */}
          <div className="flex items-center gap-2">
            <Button onClick={onOpenEmailModal} disabled={anonyme === true}>
              <Mail className="h-4 w-4 mr-2" />
              {lienEnvoye ? "Renvoyer le lien" : "Envoyer le lien"}
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Autres actions sur ce parcours"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-1">
                {peutEnvoyerResultats && (
                  <ActionMenu icone={<Send className="h-4 w-4" />} onClick={onOpenResultatsModal}>
                    Envoyer les résultats
                  </ActionMenu>
                )}
                {archive ? (
                  <ActionMenu
                    icone={<ArchiveRestore className="h-4 w-4" />}
                    onClick={onDesarchive}
                  >
                    Désarchiver
                  </ActionMenu>
                ) : (
                  <ActionMenu icone={<Archive className="h-4 w-4" />} onClick={onArchive}>
                    Archiver
                  </ActionMenu>
                )}
                {process.env.NODE_ENV !== "production" && (
                  <ActionMenu
                    icone={<KeyRound className="h-4 w-4" />}
                    onClick={() =>
                      window.open(
                        `${process.env.BUN_PUBLIC_APP_CANDIDATE_PORTAIL_URL}/p/${parcoursId}`,
                        "_blank",
                      )
                    }
                  >
                    Portail candidat
                  </ActionMenu>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Épreuves</span>
            <p className="font-medium">{libelleComposition(evaluations) || "—"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Campagne</span>
            <p className="font-medium">{titre}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Date de création</span>
            <p className="font-medium">{dateCourteHeure(dateCreation)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Dernière modification</span>
            <p className="font-medium">{dateCourteHeure(dateModification)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Lien portail</span>
            <p className="font-medium flex items-center gap-1">
              <Mail
                className={`h-4 w-4 ${lienEnvoye ? "text-etat-fait" : "text-muted-foreground/40"}`}
              />
              {lienEnvoye ? "Envoyé" : "Non envoyé"}
            </p>
          </div>
        </div>

        <div className="pt-2 border-t">
          <span className="text-muted-foreground text-sm">Description</span>
          <p className="mt-1">
            {buildParcoursDescription(
              evaluations.map((e) => ({
                typeEval: e.typeEval,
                template: e.template,
              })),
            )}
          </p>
        </div>

        {archive && (
          <div className="bg-muted p-3 rounded-surface">
            <p className="text-sm text-muted-foreground">
              ⚠️ Ce parcours est archivé. Le candidat n'y a plus accès.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
